import DlsLink from "@galaxy-io/dls/links/Link";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import Tabs from "@galaxy-io/dls/navigation/Tabs";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import { workflowName } from "@gtm/contracts/workflows/catalog";
import { InfoIcon, PlayIcon, TextAlignLeftIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import {
  DrawerList,
  DrawerRow,
  DrawerSection,
  DrawerStack,
} from "@/components/workspace/drawer-content";
import { Inline, Inset } from "@/components/workspace/people-styles";
import { RecordAudit } from "@/components/workspace/record-audit";
import { RecordQueries } from "@/components/workspace/record-queries";
import { SourceMarkdown } from "@/components/workspace/source-markdown";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { ActivitySignalChip } from "@/features/activity/activity-signal-chip";
import { ExclusionActions, ExclusionStatus } from "@/features/exclusions/exclusion-action";
import { PersonLink } from "@/features/people/person-link";
import { Avatar } from "@/features/people/profile-drawer";
import { RunControls } from "@/features/workflows/run-controls";
import { RunDetails, RunExecution, RunPayloads } from "@/features/workflows/run-details";
import { apiClient, unwrap } from "@/lib/api-client";
import { activityQueries, runQueries } from "@/lib/record-queries";
import { sourceLabel } from "@/lib/source-label";
import { date, safeUrl } from "@/lib/utils";
import { CommentContext } from "./comment-context";
import { ReviewStatusEditor, useReviews } from "./review-actions";
import { ReviewHistory } from "./review-history";

export function RecordInspectors() {
  const { params, update } = useUrlState();
  const activityId = params.get("activityId");
  const selectedActivityTab = params.get("activityTab");
  const activityTab =
    selectedActivityTab === "audit" || selectedActivityTab === "queries"
      ? selectedActivityTab
      : "overview";
  const selectedRunTab = params.get("runTab");
  const runTab =
    selectedRunTab === "audit" || selectedRunTab === "queries" ? selectedRunTab : "overview";
  const runId = params.get("runId");
  const reviews = useReviews(activityId ? [activityId] : []);
  const activity = useQuery({
    queryKey: ["activity-detail", activityId],
    enabled: !!activityId,
    queryFn: ({ signal }) =>
      unwrap(
        apiClient().GET("/activity/{id}", {
          params: { path: { id: activityId! } },
          signal,
        }),
      ),
    retry: false,
  });
  const run = useQuery({
    queryKey: ["run-detail", runId],
    enabled: !!runId,
    queryFn: ({ signal }) =>
      unwrap(
        apiClient().GET("/workflows/run", {
          params: { query: { ref: runId! } },
          signal,
        }),
      ),
    retry: false,
    refetchInterval: (query) => (query.state.data?.status === "running" ? 5000 : false),
  });
  const record = activity.data;
  return (
    <>
      <SurfaceDialog
        drawer
        flush
        open={!!activityId}
        title="People activity details"
        headerTitle={record?.title || "Activity"}
        headerIcon={InfoIcon}
        onClose={() => update({ activityId: null, activityTab: null })}
        headerActions={
          record ? (
            <ExclusionActions kind="activity" id={record.id.replace("observation:", "")} />
          ) : undefined
        }
      >
        {activity.isPending ? (
          <QueryState loadingMessage="Loading activity…" />
        ) : activity.error ? (
          <QueryState error={activity.error} onRetry={() => void activity.refetch()} />
        ) : (
          record && (
            <>
              <Tabs
                ariaLabel="Activity sections"
                value={activityTab}
                onChange={(value) => update({ activityTab: value === "overview" ? null : value })}
                items={[
                  { id: "overview", label: "Overview" },
                  { id: "queries", label: "Queries" },
                  { id: "audit", label: "Audit" },
                ]}
              />
              <DrawerStack>
                {activityTab === "queries" ? (
                  <RecordQueries queries={activityQueries(record.id)} />
                ) : activityTab === "audit" ? (
                  <>
                    <RecordAudit kind="activity" id={record.id.replace("observation:", "")} />
                    <ReviewHistory recordKey={record.id} />
                  </>
                ) : (
                  <>
                    <DrawerList>
                      <DescriptionList
                        items={[
                          {
                            label: "Status",
                            value: (
                              <>
                                <ReviewStatusEditor
                                  review={reviews.states[record.id]}
                                  busy={reviews.busy || !reviews.loaded}
                                  onAction={(action) => reviews.act([record.id], action)}
                                />
                              </>
                            ),
                          },
                          {
                            label: "Person",
                            value: record.person ? (
                              <PersonLink id={record.person.id.replace("github:", "")}>
                                <Inline>
                                  <Avatar
                                    src={record.person.avatar}
                                    name={record.person.name || record.person.login}
                                    small
                                  />
                                  <Text>{record.person.name || record.person.login}</Text>
                                </Inline>
                              </PersonLink>
                            ) : (
                              "Deleted account"
                            ),
                          },
                        ]}
                      />
                      {record.activity && (
                        <DrawerRow label="Signal">
                          <ActivitySignalChip activity={record.activity} />
                        </DrawerRow>
                      )}
                      <DescriptionList
                        items={[
                          {
                            label: "Source",
                            value: record.repositories.map((repo) => (
                              <DlsLink
                                key={repo.id}
                                href={safeUrl(repo.url) || undefined}
                                isExternal
                              >
                                {sourceLabel(record.source, repo.name)}
                              </DlsLink>
                            )),
                          },
                          {
                            label: "Occurred",
                            value: record.activity?.occurredAt
                              ? date(record.activity.occurredAt, true)
                              : "Unknown",
                          },
                          {
                            label: "Discovered",
                            value: date(record.observedAt, true),
                            family: FontFamily.MONO,
                          },
                          {
                            label: "Source link",
                            value: (
                              <>
                                <DlsLink href={safeUrl(record.url) || undefined} isExternal>
                                  {record.url}
                                </DlsLink>
                              </>
                            ),
                          },
                        ]}
                      />
                    </DrawerList>
                    {(record.commentContext || record.body) && (
                      <DrawerSection header="Description" icon={TextAlignLeftIcon} defaultIsOpen>
                        <Inset>
                          <CommentContext context={record.commentContext} asWidget />
                          {record.body && <SourceMarkdown content={record.body} />}
                        </Inset>
                      </DrawerSection>
                    )}
                    <ExclusionStatus kind="activity" id={record.id.replace("observation:", "")} />
                  </>
                )}
              </DrawerStack>
            </>
          )
        )}
      </SurfaceDialog>
      <SurfaceDialog
        drawer
        flush
        open={!!runId}
        title="Run details"
        headerTitle={run.data ? workflowName(run.data.workflowKey) : "Collection run"}
        headerDescription={
          run.data
            ? sourceLabel(run.data.workflowKey.split(".")[0], run.data.sourceName)
            : undefined
        }
        headerIcon={PlayIcon}
        headerActions={
          run.data ? (
            <RunControls
              id={run.data.workflowId}
              workflowKey={run.data.workflowKey}
              sourceId={run.data.sourceId}
              sourceName={run.data.sourceName}
              running={run.data.status === "running"}
              onComplete={() => void run.refetch()}
            />
          ) : undefined
        }
        onClose={() => update({ runId: null, runTab: null })}
      >
        {run.isPending ? (
          <QueryState loadingMessage="Loading run…" />
        ) : run.error ? (
          <QueryState error={run.error} onRetry={() => void run.refetch()} />
        ) : (
          run.data && (
            <>
              <Tabs
                ariaLabel="Run sections"
                value={runTab}
                onChange={(value) => update({ runTab: value === "overview" ? null : value })}
                items={[
                  { id: "overview", label: "Overview" },
                  { id: "queries", label: "Queries" },
                  { id: "audit", label: "Audit" },
                ]}
              />
              {runTab === "queries" ? (
                <DrawerStack>
                  <RecordQueries queries={runQueries(run.data)} />
                </DrawerStack>
              ) : runTab === "audit" ? (
                <DrawerStack>
                  {run.data.id ? (
                    <RecordAudit kind="run" id={run.data.id} />
                  ) : (
                    <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                      This run has not written anything yet.
                    </Text>
                  )}
                  <RunExecution run={run.data} />
                  <RunPayloads run={run.data} />
                </DrawerStack>
              ) : (
                <RunDetails run={run.data} />
              )}
            </>
          )
        )}
      </SurfaceDialog>
    </>
  );
}
